import { asObject, cloneValue, orderByIds, text, uniqueStrings } from './model-utils.js';

export const DRAFT_SCHEMA_VERSION = 1;
const CHAPTER_EDIT_FIELDS = ['title', 'note'];
const ITEM_EDIT_FIELDS = ['title', 'caption', 'note'];
const owns = (object, key) => Object.prototype.hasOwnProperty.call(object, key);

/** @param {string} projectId */
export function createDefaultDraft(projectId) {
  return {
    schemaVersion: DRAFT_SCHEMA_VERSION,
    projectId: text(projectId),
    chapterOrder: [],
    itemOrder: {},
    selections: { chapters: {}, items: {} },
    overrides: { chapters: {}, items: {} }
  };
}

function normalizeBooleanMap(value) {
  return Object.fromEntries(Object.entries(asObject(value)).filter(([, selected]) => typeof selected === 'boolean'));
}

function normalizeOrderMap(value) {
  return Object.fromEntries(Object.entries(asObject(value))
    .map(([id, order]) => [id, uniqueStrings(Array.isArray(order) ? order : [])])
    .filter(([, order]) => order.length));
}

function normalizeOverrides(value, allowedFields) {
  return Object.fromEntries(Object.entries(asObject(value)).map(([id, patch]) => {
    const normalized = Object.fromEntries(allowedFields
      .filter(field => owns(asObject(patch), field) && typeof patch[field] === 'string')
      .map(field => [field, patch[field]]));
    return [id, normalized];
  }).filter(([, patch]) => Object.keys(patch).length));
}

/** @param {unknown} value @param {string} [projectId] */
export function normalizeDraft(value, projectId = '') {
  const source = asObject(value);
  const expectedProjectId = text(projectId || source.projectId);
  if (Number(source.schemaVersion || DRAFT_SCHEMA_VERSION) !== DRAFT_SCHEMA_VERSION) {
    return createDefaultDraft(expectedProjectId);
  }
  return {
    schemaVersion: DRAFT_SCHEMA_VERSION,
    projectId: expectedProjectId,
    chapterOrder: uniqueStrings(Array.isArray(source.chapterOrder) ? source.chapterOrder : []),
    itemOrder: normalizeOrderMap(source.itemOrder),
    selections: {
      chapters: normalizeBooleanMap(source.selections?.chapters),
      items: normalizeBooleanMap(source.selections?.items)
    },
    overrides: {
      chapters: normalizeOverrides(source.overrides?.chapters, CHAPTER_EDIT_FIELDS),
      items: normalizeOverrides(source.overrides?.items, ITEM_EDIT_FIELDS)
    }
  };
}

/**
 * Applies the sparse draft without mutating either input.
 * Unknown draft ids are ignored; newly introduced source content is appended.
 * @param {Record<string, any>} document
 * @param {unknown} draftValue
 */
export function applyDraft(document, draftValue) {
  const source = cloneValue(document);
  const draft = normalizeDraft(draftValue, source.project?.id);
  const chapters = orderByIds(source.chapters || [], draft.chapterOrder).map(chapter => {
    const chapterPatch = draft.overrides.chapters[chapter.id] || {};
    const selected = owns(draft.selections.chapters, chapter.id)
      ? draft.selections.chapters[chapter.id]
      : chapter.selected;
    const items = orderByIds(chapter.items || [], draft.itemOrder[chapter.id] || []).map(item => {
      const itemPatch = draft.overrides.items[item.id] || {};
      return {
        ...item,
        ...itemPatch,
        selected: owns(draft.selections.items, item.id) ? draft.selections.items[item.id] : item.selected
      };
    });
    return { ...chapter, ...chapterPatch, selected, items };
  });
  return { ...source, chapters };
}

/**
 * Removes empty scaffolding before persistence. Empty strings inside an override
 * are preserved because clearing a source caption/note is a meaningful diff.
 * @param {unknown} draftValue
 */
export function compactDraft(draftValue) {
  const draft = normalizeDraft(draftValue);
  const compact = { schemaVersion: draft.schemaVersion, projectId: draft.projectId };
  if (draft.chapterOrder.length) compact.chapterOrder = draft.chapterOrder;
  if (Object.keys(draft.itemOrder).length) compact.itemOrder = draft.itemOrder;
  if (Object.keys(draft.selections.chapters).length || Object.keys(draft.selections.items).length) {
    compact.selections = {};
    if (Object.keys(draft.selections.chapters).length) compact.selections.chapters = draft.selections.chapters;
    if (Object.keys(draft.selections.items).length) compact.selections.items = draft.selections.items;
  }
  if (Object.keys(draft.overrides.chapters).length || Object.keys(draft.overrides.items).length) {
    compact.overrides = {};
    if (Object.keys(draft.overrides.chapters).length) compact.overrides.chapters = draft.overrides.chapters;
    if (Object.keys(draft.overrides.items).length) compact.overrides.items = draft.overrides.items;
  }
  return compact;
}
